import * as Y from 'yjs';

import type {
    Block,
    Data,
    Document,
    Fragment,
    FragmentMap,
    Inline,
    Mark,
    Text,
} from '@gitbook/slate';
import type { Set as ISet, List } from '@gitbook/util-immutable';
import { applyKeyedListDiff, diffKeyedLists } from '@gitbook/util-tree-merge';
import { assertNever } from '@gitbook/util-ts';

import { jsonToY, removeYXmlPrimitive, setYXmlPrimitive, updateYWithJSON } from '../utils';
import { nodeEquals } from './nodeEquals';
import {
    assertYSlateBlock,
    assertYSlateFragment,
    assertYSlateInline,
    assertYSlateText,
    type YSlateAbstractNode,
    type YSlateBlock,
    type YSlateDocument,
    type YSlateFragment,
    type YSlateInline,
    type YSlateNode,
    type YSlateText,
    type YValue,
} from './utils';

/**
 * Convert a slate document to a Yjs map. It should aim to produce as few operations as necessary
 * whilst maintaining the consistency of the document.
 */
export function documentToY(document: Document, yDocument: YSlateDocument): YSlateDocument {
    setYXmlPrimitive(yDocument, 'key', document.key);
    setDataInY(yDocument, document.data);
    setNodesInY(yDocument, document.nodes);

    return yDocument;
}

/**
 * Convert a slate node to a Yjs map.
 */
export function nodeToY(node: Block | Inline | Text): YSlateNode {
    const nodeObject = node.object;
    switch (nodeObject) {
        case 'block':
            return blockToY(node);
        case 'inline':
            return inlineToY(node);
        case 'text':
            return textToY(node);
        default:
            throw new Error(`Unknown node type: ${nodeObject}`);
    }
}

/**
 * Set all nodes in a Yjs element to the Slate nodes.
 */
function setNodesInY(
    yNodes: YSlateBlock | YSlateFragment | YSlateInline | YSlateDocument,
    nodes: List<Block | Inline | Text>
) {
    if (!yNodes.doc) {
        // When the doc is not initialized, we can only replace
        return replaceNodesInY(yNodes, nodes);
    }

    applyKeyedListDiff(
        diffKeyedLists<
            YSlateBlock | YSlateFragment | YSlateInline | YSlateText,
            Block | Inline | Text
        >(
            {
                length: yNodes.length,
                at(index) {
                    return yNodes.get(index);
                },
                slice(start, end) {
                    return yNodes.slice(start, end);
                },
            },
            nodes.toArray(),
            {
                // @ts-expect-error
                getIdBefore: (item) => item.getAttribute('key'),
                // @ts-expect-error
                getIdAfter: (item) => item.get('key'),
                areEquals: (a, b) => {
                    return nodeEquals(a, b);
                },
            }
        ),
        {
            insert: (index, items) => {
                yNodes.insert(
                    index,
                    // @ts-expect-error
                    items.map((n) => nodeToY(n))
                );
            },
            delete: (index, length) => {
                yNodes.delete(index, length);
            },
            update: (_index, before, after) => {
                switch (after.object) {
                    case 'block':
                        assertYSlateBlock(before);
                        blockToY(after, before);
                        break;
                    case 'inline':
                        assertYSlateInline(before);
                        inlineToY(after, before);
                        break;
                    case 'text':
                        assertYSlateText(before);
                        textToY(after, before);
                        break;
                    default:
                        assertNever(after);
                }
            },
        }
    );
}

/**
 * Replace all nodes in a Yjs element with the Slate nodes.
 */
function replaceNodesInY(
    yNodes: YSlateBlock | YSlateFragment | YSlateInline | YSlateDocument,
    nodes: List<Block | Inline | Text>
) {
    if (yNodes.doc) {
        yNodes.delete(0, yNodes.length);
    }
    if (nodes.size > 0) {
        yNodes.insert(
            0,
            // @ts-expect-error
            nodes
                .toArray()
                .map((n) => nodeToY(n))
        );
    }
}

/**
 * Convert a slate block to a Yjs map.
 */
function blockToY(block: Block, yNode: YSlateBlock = new Y.XmlElement(block.object)): YSlateBlock {
    setYXmlPrimitive(yNode, 'key', block.key);
    setYXmlPrimitive(yNode, 'type', block.type);
    if (block.isVoid) {
        setYXmlPrimitive(yNode, 'isVoid', block.isVoid);
    } else {
        removeYXmlPrimitive(yNode, 'isVoid');
    }

    setDataInY(yNode, block.data);
    setNodesInY(yNode, block.nodes);
    setFragmentsInY(yNode, block.fragments);

    return yNode;
}

/**
 * Convert a slate inline to a Yjs map.
 */
function inlineToY(
    inline: Inline,
    yNode: YSlateInline = new Y.XmlElement(inline.object)
): YSlateInline {
    setYXmlPrimitive(yNode, 'key', inline.key);
    setYXmlPrimitive(yNode, 'type', inline.type);
    if (inline.isVoid) {
        setYXmlPrimitive(yNode, 'isVoid', inline.isVoid);
    } else {
        removeYXmlPrimitive(yNode, 'isVoid');
    }

    setDataInY(yNode, inline.data);
    setNodesInY(yNode, inline.nodes);
    setFragmentsInY(yNode, inline.fragments);

    return yNode;
}

function setFragmentsInY(yNode: YSlateInline | YSlateBlock, fragments: FragmentMap) {
    if (fragments.size === 0) {
        removeYXmlPrimitive(yNode, 'fragments');
        return;
    }

    let yFragments = yNode.doc ? yNode.getAttribute('fragments') : undefined;

    if (yFragments) {
        setDiffFragmentsInY(yFragments, fragments);
    } else {
        yFragments = new Y.XmlFragment();
        yNode.setAttribute('fragments', yFragments);
        replaceFragmentsInY(yFragments, fragments);
    }
}

/**
 * Set fragments by diffing them
 */
function setDiffFragmentsInY(yFragments: Y.XmlFragment, fragments: FragmentMap) {
    applyKeyedListDiff(
        diffKeyedLists<YSlateFragment | YSlateText, Fragment>(
            {
                length: yFragments.length,
                at(index) {
                    return yFragments.get(index);
                },
                slice(start, end) {
                    return yFragments.slice(start, end);
                },
            },
            fragments.toValuesArray(),
            {
                getIdBefore: (yItem) => yItem.getAttribute('key'),
                getIdAfter: (item) => item.get('key'),
                areEquals: (a, b) => {
                    return nodeEquals(a, b);
                },
            }
        ),
        {
            insert: (index, items) => {
                yFragments.insert(
                    index,
                    items.map((n) => fragmentToY(n))
                );
            },
            delete: (index, length) => {
                yFragments.delete(index, length);
            },
            update: (_index, before, after) => {
                assertYSlateFragment(before);
                fragmentToY(after, before);
            },
        }
    );
}

/**
 * Replace fragments in a Yjs element with the given fragments.
 */
function replaceFragmentsInY(yFragments: Y.XmlFragment, fragments: FragmentMap) {
    if (yFragments.doc) {
        yFragments.delete(0, yFragments.length);
    }

    if (fragments.size > 0) {
        yFragments.insert(
            0,
            fragments.toValuesArray().map((fragment) => fragmentToY(fragment))
        );
    }
}

/**
 * Convert a slate block to a Yjs map.
 */
export function fragmentToY(
    fragment: Fragment,
    yNode: YSlateFragment = new Y.XmlElement(fragment.object)
): YSlateFragment {
    setYXmlPrimitive(yNode, 'key', fragment.key);
    fragment.type && setYXmlPrimitive(yNode, 'type', fragment.type);
    fragment.fragment && setYXmlPrimitive(yNode, 'fragment', fragment.fragment);

    setNodesInY(yNode, fragment.nodes);

    return yNode;
}

/**
 * Convert a slate text node to a Yjs map.
 */
function textToY(text: Text, yElement: YSlateText = new Y.XmlText()): YSlateText {
    let index = 0;

    setYXmlPrimitive(yElement, 'key', text.key);

    if (yElement.doc) {
        yElement.delete(0, yElement.length);
    }

    text.leaves.forEach((leaf) => {
        const attributes = marksToAttributes(leaf.marks);
        const textValue = leaf.text;
        yElement.insert(index, textValue, attributes);

        index += textValue.length;
    });

    return yElement;
}

/**
 * Convert a set of marks to attributes that can be set on a Yjs text.
 */
export function marksToAttributes(marks: ISet<Mark>): object {
    if (marks.size === 0) {
        return {};
    }

    const result = {};
    marks.forEach((mark) => {
        result[mark.type] = mark.data.toObject();
    });

    return result;
}

/**
 * Write node data into the Yjs node.
 */
export function setDataInY(yNode: YSlateAbstractNode, data: Data) {
    if (data.size === 0) {
        if (yNode.doc && yNode.hasAttribute('data')) {
            yNode.removeAttribute('data');
        }

        return;
    }

    const yData = yNode.doc ? yNode.getAttribute('data') : undefined;
    if (!yData) {
        const newYData = new Y.Map<YValue>();
        yNode.setAttribute('data', newYData);
        data.forEach((value, key) => {
            newYData.set(key, jsonToY(value));
        });
    } else {
        updateYWithJSON(yData, data.toObject());
    }
}
